// frontend/src/pages/Sabesp/components/MessageBubble.js
import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SourcesPanel from "./SourcesPanel.js";

const BADGE_LABEL = {
  running: "Executando...",
  completed: "Concluído com sucesso",
  cancelled: "Cancelado pelo usuário",
  failed: "Erro na execução",
};

// Remove [src-xxxx] (ou varios agrupados por virgula) do texto pra copia --
// mesma regex de prepareCitationLinks (abaixo), mas descartando a tag por
// completo em vez de transformar em link, ja que fora da tela nao ha nada
// pra ela apontar.
function stripCitationTags(text) {
  return (text || "")
    .replace(/\s*\[\s*src-[a-z0-9-]+(?:\s*,\s*src-[a-z0-9-]+)*\s*\]/gi, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

// Substitui [src-xxxx] (ou varios agrupados por virgula) por links markdown
// de verdade apontando pra um href fake "#cit-<id>" — o componente `a`
// customizado abaixo intercepta esse padrao e desenha o <sup> numerado
// clicavel em vez de uma ancora normal. Isso evita precisar de HTML bruto
// (dangerouslySetInnerHTML), que o react-markdown nao expõe por padrao.
function prepareCitationLinks(markdownText, citations) {
  const indexById = {};
  (citations || []).forEach((c, i) => {
    indexById[c.citation_id] = i + 1;
  });

  return (markdownText || "").replace(
    /\[\s*(src-[a-z0-9-]+(?:\s*,\s*src-[a-z0-9-]+)*)\s*\]/gi,
    (_m, group) => {
      const links = group
        .split(",")
        .map((part) => part.trim())
        .map((id) => {
          const num = indexById[id];
          return num ? `[${num}](#cit-${id})` : "";
        })
        .filter(Boolean);
      return links.length ? " " + links.join("") + " " : "";
    }
  );
}

function CitationOrLink({ href, children, onOpenCitationById }) {
  if (typeof href === "string" && href.startsWith("#cit-")) {
    const citationId = href.slice("#cit-".length);
    return (
      <sup
        className="sabesp-cit-ref"
        title={`Ver fonte ${children}`}
        onClick={(e) => {
          e.preventDefault();
          onOpenCitationById(citationId);
        }}
      >
        {children}
      </sup>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

export default function MessageBubble({ message, token, onOpenCitationById, onOpenCitation, onOpenFigure, onFeedback }) {
  const isUser = message.role === "user";

  // Hooks precisam rodar incondicionalmente em toda renderizacao (Rules of
  // Hooks) — por isso ficam antes do branch por role, mesmo que so o ramo
  // "agent" use os valores.
  const preparedText = useMemo(
    () => (isUser ? "" : prepareCitationLinks(message.text, message.citations)),
    [isUser, message.text, message.citations]
  );

  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(stripCitationTags(message.text));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (isUser) {
    return (
      <div className="sabesp-message sabesp-message-user">
        <div className="sabesp-message-bubble">
          {message.text.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  const badgeStatus = message.status;
  const badgeClass = `sabesp-agent-badge sabesp-badge-${badgeStatus}`;

  return (
    <div className="sabesp-message sabesp-message-agent">
      <div className="sabesp-message-bubble">
        <div className="sabesp-agent-meta-container">
          <div className={badgeClass}>
            {badgeStatus === "running" && <span className="sabesp-spinner"></span>}
            <span className="sabesp-badge-text">{BADGE_LABEL[badgeStatus] || badgeStatus}</span>
          </div>
        </div>

        {message.logsVisible && message.logs.length > 0 && (
          <div className="sabesp-console-logs">
            {message.logs.map((log, i) => (
              <div className={`sabesp-log-line sabesp-log-${log.category || "info"}`} key={i}>
                {log.text}
              </div>
            ))}
          </div>
        )}

        {message.error && (
          <div className="sabesp-error-box sabesp-error-box-general">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div style={{ flex: 1 }}>
              <div className="sabesp-error-title">Falha na Execução</div>
              <div style={{ fontSize: "0.8rem" }}>{message.error}</div>
            </div>
          </div>
        )}

        {message.text && (
          <div className="sabesp-agent-response-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: (props) => <CitationOrLink {...props} onOpenCitationById={onOpenCitationById} />,
              }}
            >
              {preparedText}
            </ReactMarkdown>
          </div>
        )}

        <SourcesPanel
          citations={message.citations}
          figures={message.figures}
          token={token}
          onOpenCitation={onOpenCitation}
          onOpenFigure={onOpenFigure}
        />

        {badgeStatus === "completed" && message.text && (
          <div className="sabesp-message-feedback-row">
            <button
              className={`sabesp-btn-feedback${message.feedback === "like" ? " active" : ""}`}
              title="Resposta útil"
              onClick={() => onFeedback("like")}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
              </svg>
            </button>
            <button
              className={`sabesp-btn-feedback${message.feedback === "dislike" ? " active" : ""}`}
              title="Resposta não útil"
              onClick={() => onFeedback("dislike")}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"></path>
              </svg>
            </button>
            <button className="sabesp-btn-feedback" title="Copiar resposta" onClick={handleCopy}>
              {copied ? (
                "Copiado!"
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
